/**
 * MemberRolesField - composite editor for the set of roles a member holds.
 *
 * A member has one role by default; the "Add role" button appends another
 * dropdown so an admin can grant additional roles (e.g. an "LOA Approver"
 * role on top of the member's day-to-day role - authorization unions the
 * permissions of every held role). A genuinely composite widget embedded in
 * the generated form via `type: 'custom'` (per the Generated Form Rules);
 * every control is a shared component. Role options arrive from the API -
 * zero hardcoded values.
 */

import { X, Plus } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

export interface MemberRolesFieldOption {
  uuid: string
  name: string
  is_admin: boolean
}

interface MemberRolesFieldProps {
  /** Selected role UUIDs, one per dropdown row (at least one). */
  value: string[]
  onChange: (next: string[]) => void
  options: MemberRolesFieldOption[]
  /** When true, roles are shown read-only (e.g. the organisation owner). */
  readOnly?: boolean
  addLabel: string
  placeholder: string
}

export function MemberRolesField({
  value,
  onChange,
  options,
  readOnly = false,
  addLabel,
  placeholder,
}: MemberRolesFieldProps) {
  const rows = value.length > 0 ? value : ['']

  const optionLabel = (option: MemberRolesFieldOption) =>
    option.is_admin ? `${option.name} · ADMIN` : option.name

  const updateRow = (index: number, roleUuid: string) => {
    onChange(rows.map((current, i) => (i === index ? roleUuid : current)))
  }

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index))
  }

  const addRow = () => {
    onChange([...rows, ''])
  }

  return (
    <div className="col-span-2 flex flex-col gap-2.5">
      {rows.map((roleUuid, index) => {
        // Roles picked in the other rows are unavailable here (no duplicates).
        const takenElsewhere = new Set(
          rows.filter((_, i) => i !== index).filter(Boolean),
        )
        const available = options.filter(
          (option) => option.uuid === roleUuid || !takenElsewhere.has(option.uuid),
        )
        return (
          <div key={index} className="flex items-center gap-2">
            <Select
              value={roleUuid}
              disabled={readOnly}
              onValueChange={(next) => updateRow(index, next ?? '')}
            >
              <SelectTrigger
                data-testid={`member-role-select-${index}`}
                className="flex-1"
                aria-label={`Role ${index + 1}`}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {available.map((option) => (
                  <SelectItem key={option.uuid} value={option.uuid}>
                    {optionLabel(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!readOnly && rows.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                aria-label="Remove role"
                data-testid={`member-role-remove-${index}`}
                onClick={() => removeRow(index)}
                className="shrink-0 text-[var(--ink-3)]"
              >
                <X size={14} />
              </Button>
            )}
          </div>
        )
      })}

      {!readOnly && rows.every(Boolean) && rows.length < options.length && (
        <div>
          <Button
            type="button"
            variant="outline"
            size="xs"
            data-testid="member-role-add"
            onClick={addRow}
            className="flex items-center gap-1"
          >
            <Plus size={12} />
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  )
}
